import {
  ActivityFilterKey,
  ActivityFilterModel,
  ActivityInboxType,
  ActivityOrderBy,
  getActivityFilter,
  SortDirectionEnum,
  useActivityListFilter,
  useCurrentAccount,
  useCurrentWorkspace,
  useFindUnreadActivityInbox,
  useSetActivityListFilter,
} from '@gripp/shared-logic';
import { createColumnHelper } from '@tanstack/react-table';
import { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Platform,
  StyleProp,
  StyleSheet,
  Text,
  View,
  ViewStyle,
} from 'react-native';
import { useParams } from 'react-router-dom';
import { Colors } from '../../themes/colors';
import { InfiniteDataGrid } from '../infiniteDataGrid';
import { ActivitySortDropdown } from './activitySortDropdown';
import { IssueCard, IssueCardData } from './issueCard';

export type ActivityListProps = {
  assetId?: string;
  showAssetName?: boolean;
  onIssueSelected?: (id: string) => void;
  onActivityCountChange?: (count: number) => void;
  selectedIssue?: string;
  listMaxHeight?: number;
  showScrollIndicator?: boolean;
  containerStyle?: StyleProp<ViewStyle>;
  right?: FC;
  filterKey: ActivityFilterKey;
  showTextEmpty?: boolean;
  excludeFilter?: boolean;
  onDataLoaded?: (data: any) => void;
  customEmptyState?: React.ReactNode;
};

const getOrderByField = (orderBy: ActivityOrderBy): string => {
  if (orderBy === ActivityOrderBy.MostRecentUpdated) {
    return 'updatedAt';
  }
  return orderBy;
};

export const ActivityList = (props: ActivityListProps) => {
  const { data: filterData, isLoading: isLoadingFilter } =
    useActivityListFilter(props.filterKey);
  const { account } = useCurrentAccount();

  const { mutateAsync: setActivityListFilter } = useSetActivityListFilter(
    props.filterKey
  );

  const { t } = useTranslation();
  const [filter, setFilter] = useState(undefined);
  const [inbox, setInbox] = useState<any[]>([]);
  const [itemsCount, setItemsCount] = useState(0);
  const { issueId } = useParams();

  const { data: inboxData, isLoading: isLoadingInbox } =
    useFindUnreadActivityInbox();

  const workspace = useCurrentWorkspace(true);

  const currentOrderBy =
    filterData?.orderBy || ActivityOrderBy.MostRecentUpdated;
  const orderByField = getOrderByField(currentOrderBy);

  useEffect(() => {
    if (inboxData) {
      setInbox(inboxData.items);
    }
  }, [inboxData]);

  useEffect(() => {
    if (!filterData || workspace === undefined) return;

    // reset filtering only for Recent Activity
    const aFilter = props.excludeFilter
      ? new ActivityFilterModel()
      : filterData;

    const f = getActivityFilter(aFilter, workspace?.id, props.assetId);

    setFilter(f);
  }, [filterData, workspace, props.assetId]);

  const hasUnread = useCallback(
    (activity: any) => {
      return inbox.some(
        (i) =>
          (i.type === ActivityInboxType.IssueUpdated ||
            i.type === ActivityInboxType.IssueMessageCreated) &&
          i.data.id === activity.id
      );
    },
    [inbox]
  );

  const isMentionedInInbox = useCallback(
    (activity: any) => {
      const inboxItem = inbox.find(
        (i) =>
          i.type === ActivityInboxType.IssueMessageCreated &&
          i.data.id === activity.id
      );
      return inboxItem?.data?.isMentioned || false;
    },
    [inbox]
  );

  const rowStyle = (row: any) => {
    const isUnread = hasUnread(row.original);
    const isMentioned = isMentionedInInbox(row.original);

    return [
      styles.row,
      isUnread && isMentioned ? styles.unread : styles.read,
      row.original.id === issueId && {
        backgroundColor: Colors.blackWithAlpha,
      },
    ];
  };

  const columnHelper = createColumnHelper<any>();

  const columns: any[] = [
    columnHelper.accessor('id', {
      cell: ({ row }) => {
        const activity = row.original as IssueCardData;
        return (
          activity && (
            <IssueCard
              account={account}
              activity={activity}
              showAssetName={props.showAssetName}
              isUnread={hasUnread(activity)}
              isMentionedInInbox={isMentionedInInbox(activity)}
              filter={filterData}
            />
          )
        );
      },
      meta: {
        style: styles.cell,
        customRender: true,
      },
    }),
  ];
  if (props.right) {
    const RightComponent = props.right;
    columns.push(
      columnHelper.accessor('type', {
        cell: ({ row }) => {
          const activity = row.original as IssueCardData;
          return activity && (RightComponent as any)({ activity });
        },
        meta: {
          style: styles.right,
          customRender: true,
        },
      })
    );
  }

  const onActivitySelected = (activity: any) => {
    props.onIssueSelected && props.onIssueSelected(activity.id);
  };

  const handleSortChange = async (newSort: ActivityOrderBy) => {
    if (filterData) {
      await setActivityListFilter(
        new ActivityFilterModel({
          ...filterData,
          orderBy: newSort,
        })
      );
    }
  };

  if (isLoadingFilter || isLoadingInbox) {
    return null;
  }

  return !filter ? null : (
    <View style={styles.container}>
      <View
        style={
          Platform.OS === 'web'
            ? styles.desktopStickyHeader
            : styles.mobileStickyHeader
        }
      >
        <Text style={styles.resultsText}>
          {itemsCount} {t('activity.topBar.results')}
        </Text>
        <ActivitySortDropdown
          selectedSort={currentOrderBy}
          onSortChange={handleSortChange}
        />
      </View>
      <InfiniteDataGrid
        columns={columns}
        query={getActivities}
        dataPrefix="issue.findIssues"
        modelName="issue"
        onRowClick={onActivitySelected}
        orderBy={{
          field: orderByField,
          order: SortDirectionEnum.Desc,
        }}
        pageSize={50}
        filter={filter}
        hideHeader={true}
        hideResultsLabel={true}
        refetchOnFocus={true}
        listMaxHeight={props.listMaxHeight as number}
        showScrollIndicator={props.showScrollIndicator}
        containerStyle={[props.containerStyle, { paddingTop: 50 }]}
        rowStyle={rowStyle}
        textEmpty={
          props.customEmptyState ||
          ((workspace || Platform.OS === 'web' || props.showTextEmpty) &&
            t('activity.textEmpty'))
        }
        onItemsCountChange={(count) => {
          setItemsCount(count);
          props.onActivityCountChange?.(count);
        }}
        enableScrollRefresh={true}
        onDataLoaded={props.onDataLoaded}
      />
    </View>
  );
};

const getActivities = `
    query FindIssues($filter: FindIssuesFilter, $page: PageRequest, $orderBy: OrderByInput) {
      issue {
        findIssues(filter: $filter, page: $page, orderBy: $orderBy) {
          count
          offset
          limit
          items {
            id
            severity
            title
            subtitle
            isPinned
            asset {
              name
              coverImage {
                id
                extension
                status
                type
              }
            }
            workspace {
              name
            }
            labels {
              id
              name
            }
            createdAt
            effectiveCreationDate
            createdBy {
              id
              name {
                first
                last
              }
            }
            updatedAt
            updatedBy {
              id
              name {
                first
                last
              }
            }
            nextReminderDate
            userMentions {
              id
              name {
                first
                last
              }
            }
            latestMessage {
              text
              i18n
              createdBy {
                id
                name {
                  first
                  last
                }
              }
              userMentions {
                id
                name {
                  first
                  last
                }
              }
              reactions {
                id
                createdAt
                createdBy 
                reactionType
              }
              type
            }
          }
        }
      }
    }
  `;

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  desktopStickyHeader: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
    backgroundColor: Colors.white,
    borderBottomWidth: 1,
    borderTopWidth: 1,
    borderColor: Colors.secondaryGrayHeader,
    paddingHorizontal: 16,
    paddingVertical: 12,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: 50,
  },
  mobileStickyHeader: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
    backgroundColor: Colors.white,
    borderBottomWidth: 1,
    borderTopWidth: 1,
    borderColor: Colors.secondaryGrayHeader,
    paddingHorizontal: 16,
    paddingVertical: 12,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: 50,
  },
  resultsText: {
    fontSize: 16,
    fontWeight: '500',
    color: Colors.grayText,
    letterSpacing: -0.25,
  },
  cell: {
    alignSelf: 'stretch',
    alignContent: 'stretch',
    width: '100%',
  },
  row: {
    paddingLeft: 0,
    paddingRight: 0,
    borderBottomWidth: 1,
    borderBottomColor: Colors.secondaryGrayHeader,
    height: 'auto',
    minHeight: 120,
  },
  right: {
    maxWidth: 5,
  },
  read: {
    backgroundColor: Colors.white,
  },
  unread: {
    backgroundColor: Colors.unreadBackground,
  },
  emptyContainer: {
    flexDirection: 'column',
    rowGap: 40,
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
    width: '100%',
  },
  textWrapper: {
    width: '100%',
  },
  emptyTapText: {
    fontWeight: '700',
    fontSize: 22,
    textAlign: 'center',
  },
});
